import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Activity, Pill, Microscope } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const HeroSlider = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const { t, isRTL } = useLanguage();

  const slides = [
    {
      id: 1,
      title: t('slide1Title'),
      description: t('slide1Desc'),
      image: 'https://images.unsplash.com/photo-1559757148-5c350d0d3c56?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80',
      icon: <Activity className="w-12 h-12 text-primary-500" />,
      color: 'from-primary-600 to-primary-800'
    },
    {
      id: 2,
      title: t('slide2Title'),
      description: t('slide2Desc'),
      image: 'https://images.unsplash.com/photo-1585435557343-3b092031d4fe?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80',
      icon: <Pill className="w-12 h-12 text-medical-teal" />,
      color: 'from-medical-teal to-primary-700'
    },
    {
      id: 3,
      title: t('slide3Title'),
      description: t('slide3Desc'),
      image: 'https://images.unsplash.com/photo-1582719471384-894fbb16e074?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80',
      icon: <Microscope className="w-12 h-12 text-accent-500" />,
      color: 'from-accent-600 to-accent-800'
    }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);

    return () => clearInterval(timer);
  }, [slides.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  return (
    <div className="relative h-64 sm:h-80 md:h-96 lg:h-[500px] xl:h-[600px] overflow-hidden rounded-lg sm:rounded-2xl shadow-lg sm:shadow-2xl">
      <AnimatePresence mode="wait">
        <motion.div
          key={currentSlide}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.8 }}
          className="absolute inset-0"
        >
          {/* Background Image */}
          <div 
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{ backgroundImage: `url(${slides[currentSlide].image})` }}
          />
          
          {/* Overlay */}
          <div className={`absolute inset-0 bg-gradient-to-r ${slides[currentSlide].color} opacity-85`} />
          
          {/* Content */}
          <div className={`relative h-full flex items-center ${isRTL ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 ${isRTL ? 'text-right' : 'text-left'}`}>
              <motion.div
                initial={{ y: 30, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.3, duration: 0.8 }}
                className="max-w-2xl"
              >
                <div className="mb-3 sm:mb-6">
                  <div className="w-8 h-8 sm:w-12 sm:h-12 text-primary-500">
                    {slides[currentSlide].icon}
                  </div>
                </div>
                
                <h1 className={`text-xl sm:text-2xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-white mb-3 sm:mb-6 leading-tight ${isRTL ? 'font-arabic' : 'font-english'}`}>
                  {slides[currentSlide].title}
                </h1>
                
                <p className={`text-sm sm:text-lg md:text-xl lg:text-2xl text-gray-200 mb-4 sm:mb-8 leading-relaxed ${isRTL ? 'font-arabic' : 'font-english'}`}>
                  {slides[currentSlide].description}
                </p>
                
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-white text-primary-700 px-4 sm:px-8 py-2 sm:py-4 rounded-md sm:rounded-lg font-semibold text-sm sm:text-lg hover:bg-gray-100 transition-colors shadow-lg"
                >
                  {t('exploreProducts') || 'Explore Products'}
                </motion.button>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Navigation Buttons */}
      <button
        onClick={prevSlide}
        className={`absolute top-1/2 transform -translate-y-1/2 ${isRTL ? 'right-2 sm:right-4' : 'left-2 sm:left-4'} bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white p-2 sm:p-3 rounded-full transition-colors z-10`}
      >
        {isRTL ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}
      </button>
      
      <button
        onClick={nextSlide}
        className={`absolute top-1/2 transform -translate-y-1/2 ${isRTL ? 'left-2 sm:left-4' : 'right-2 sm:right-4'} bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white p-2 sm:p-3 rounded-full transition-colors z-10`}
      >
        {isRTL ? <ChevronLeft size={20} /> : <ChevronRight size={20} />}
      </button>

      {/* Dots Indicator */}
      <div className="absolute bottom-3 sm:bottom-6 left-1/2 transform -translate-x-1/2 flex space-x-2 sm:space-x-3">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentSlide(index)}
            className={`w-2 h-2 sm:w-3 sm:h-3 rounded-full transition-all ${
              index === currentSlide 
                ? 'bg-white scale-125' 
                : 'bg-white/50 hover:bg-white/75'
            }`}
          />
        ))}
      </div>
    </div>
  );
};

export default HeroSlider;